<script setup lang="ts">
    import { useLoadedProduct } from '@/composables/useLoadedProduct';
    import BaseButton from '@/components/BaseButton.vue';
    import TheCard3DPanel from '@/components/TheCard3DPanel.vue';
    import { useConfigStore } from '@/store/config.store';
    import { useOrderStore } from '@/store/order.store';
    import { useProductHas3d } from '@/composables/useProductHas3d';
    import BaseIcon from '@/components/BaseIcon.vue';
    import { useEditor2D } from '@/composables/useEditor2D';
    import { CARD_FOLD_DEFAULT, CARD_FOLD_MAX } from '@/constants/cardFold';
    import { EProductFormat } from '@/domain/product/EProductFormat';
    import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
    import { storeToRefs } from 'pinia';
    import { useI18n } from 'vue-i18n';
    import type { TSide } from '@/domain/shared/TSide';

    defineEmits<{ close: [] }>();

    const { t } = useI18n();

    const { currentSide, isAdmin } = storeToRefs(useConfigStore());
    const { hasTwoFaces } = storeToRefs(useOrderStore());
    const product = useLoadedProduct();
    const { productHas3DModel } = useProductHas3d();
    // DC1-03 (signed quote, 08/10/2026): a pitcher printed on both faces shows its two faces
    // side by side on the proof, each under its name.
    const isFacePair = computed(() => productHas3DModel.value && hasTwoFaces.value);
    const FACE_PAIR: TSide[] = ['recto', 'verso'];
    const pairSrcs = ref<Partial<Record<TSide, string>>>({});
    const is3DMode = ref(!productHas3DModel.value);
    const foldAngle = ref(CARD_FOLD_DEFAULT);
    // Only cards in Folded form actually hinge — cut-shape cards and
    // non-Card products stay flat, so no fold slider for them.
    const isFoldable = computed(() => product.value?.canFold ?? false);
    // In the back-office's editor, the proof is the print file itself, without the watermark the
    // customer's proof carries, and each face can be downloaded (Katia, 2026-10-09: "on est en
    // mode édition pas en mode utilisateur").
    const canDownload = computed(() => isAdmin.value);
    const previewSide = ref<TSide>(currentSide.value);
    const imgSrc = ref();
    const isLoading = ref(false);

    watch(previewSide, updateImage);

    onMounted(() => {
        if (isFacePair.value) {
            updateFacePair();
            return;
        }
        updateImage(previewSide.value);
    });

    onUnmounted(() => {
        if (imgSrc.value) {
            URL.revokeObjectURL(imgSrc.value);
        }
        Object.values(pairSrcs.value).forEach((src) => URL.revokeObjectURL(src));
    });

    async function exportFace(side: TSide): Promise<Blob | undefined> {
        const exports = useEditor2D(side)?.exports;
        if (!exports) {
            console.error(`PreviewBATModal: no editor for side ${side}, nothing to show`);
            return undefined;
        }
        return isAdmin.value ? ((await exports.exportPrintBlob()) ?? undefined) : exports.exportWatermarkedPreview();
    }

    // ECO300.png for a cup, ECOJAR100-recto.png and ECOJAR100-verso.png for a pitcher's faces.
    function fileNameOf(side?: TSide): string {
        return side && (isFacePair.value || product.value.format === EProductFormat.RECTO_VERSO)
            ? `${product.value.name}-${side}.png`
            : `${product.value.name}.png`;
    }

    async function updateFacePair() {
        isLoading.value = true;
        try {
            const blobs = await Promise.all(FACE_PAIR.map(exportFace));
            pairSrcs.value = Object.fromEntries(
                FACE_PAIR.flatMap((side, index) => {
                    const blob = blobs[index];
                    return blob ? [[side, URL.createObjectURL(blob)]] : [];
                }),
            );
        } finally {
            isLoading.value = false;
        }
    }

    async function updateImage(format: 'recto' | 'verso' = previewSide.value) {
        isLoading.value = true;
        try {
            const blob = await exportFace(format);
            if (blob) {
                if (imgSrc.value) {
                    URL.revokeObjectURL(imgSrc.value);
                }
                imgSrc.value = URL.createObjectURL(blob);
            }
        } finally {
            isLoading.value = false;
        }
    }
</script>
<template>
    <div
        data-test="bat-modal"
        class="fixed z-40 flex h-screen w-screen items-center justify-center bg-black/30"
        tabindex="0"
        role="button"
        aria-label="Close modal"
        @click.self="$emit('close')"
        @keydown.self.escape="$emit('close')"
    >
        <div
            class="relative flex w-full flex-col items-center gap-4 bg-transparent px-2 py-4 lg:h-5/6 lg:w-2/3 lg:p-6"
            role="button"
            tabindex="0"
            @click.self="$emit('close')"
            @keydown.self.escape="$emit('close')"
        >
            <div class="flex min-h-0 w-full flex-1 items-center justify-center">
                <div
                    v-if="isFacePair"
                    class="grid h-full w-full grid-cols-2 gap-3 lg:gap-6"
                    data-test="bat-face-pair"
                >
                    <figure
                        v-for="side in FACE_PAIR"
                        :key="side"
                        class="border-primary flex min-h-0 flex-col border border-dashed bg-white"
                    >
                        <div
                            v-if="isLoading"
                            class="flex-1 animate-pulse bg-gray-100"
                        />
                        <img
                            v-else
                            :src="pairSrcs[side]"
                            :alt="t('bat_preview')"
                            :data-test="`bat-preview-face-${side}`"
                            class="min-h-0 w-full flex-1 object-contain"
                        />
                        <figcaption class="flex items-center justify-center gap-3 py-2 text-xs font-bold text-gray-700">
                            {{ side === 'recto' ? t('faces_front') : t('faces_back') }}
                            <a
                                v-if="canDownload && pairSrcs[side]"
                                :href="pairSrcs[side]"
                                :download="fileNameOf(side)"
                                class="text-primary hover:text-primary-dark flex items-center gap-1 font-semibold transition active:scale-95"
                                :data-test="`bat-download-${side}`"
                            >
                                <BaseIcon
                                    name="ArrowDownTrayIcon"
                                    type="outlined"
                                    class="h-4 w-4"
                                />
                                {{ t('bat_download_image') }}
                            </a>
                        </figcaption>
                    </figure>
                </div>
                <div
                    v-else-if="!is3DMode || productHas3DModel"
                    class="border-primary flex h-full w-full flex-col items-center border border-dashed bg-white p-0 leading-4 lg:min-h-0 lg:w-fit"
                >
                    <div
                        v-if="isLoading"
                        data-test="bat-preview-loading"
                        class="h-full w-full animate-pulse bg-gray-100"
                    />
                    <img
                        v-else
                        :src="imgSrc"
                        :alt="t('bat_preview')"
                        data-test="bat-preview-image"
                        class="max-h-full w-full max-w-full object-contain lg:h-full lg:w-auto"
                    />
                </div>

                <div
                    v-if="is3DMode && !productHas3DModel"
                    class="relative h-full w-full"
                >
                    <TheCard3DPanel
                        inline
                        :fold-angle
                    />
                    <div
                        v-if="isFoldable"
                        class="absolute bottom-4 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-xl bg-white/90 px-3 py-1.5 shadow-md ring-1 ring-gray-200 backdrop-blur-sm"
                    >
                        <svg
                            class="h-3 w-3 flex-none text-gray-400"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M9 18l6-6-6-6"
                            />
                        </svg>
                        <label
                            for="preview-fold-slider"
                            class="sr-only"
                            >{{ t('fold') }}</label
                        >
                        <input
                            id="preview-fold-slider"
                            data-test="card-fold-slider"
                            type="range"
                            :value="foldAngle"
                            :min="0"
                            :max="CARD_FOLD_MAX"
                            step="0.01"
                            class="accent-primary w-28 cursor-pointer"
                            @input="foldAngle = Number(($event.target as HTMLInputElement).value)"
                        />
                        <svg
                            class="h-3 w-3 flex-none text-gray-400"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M15 18l-6-6 6-6"
                            />
                        </svg>
                    </div>
                </div>
            </div>

            <a
                v-if="canDownload && !isFacePair && imgSrc && !isLoading"
                :href="imgSrc"
                :download="fileNameOf(product.format === EProductFormat.RECTO_VERSO ? previewSide : undefined)"
                class="text-primary hover:bg-primary/5 flex h-10 items-center gap-2 rounded-full bg-white px-5 text-sm font-semibold shadow-md ring-1 ring-gray-200 transition active:scale-95"
                data-test="bat-download"
            >
                <BaseIcon
                    name="ArrowDownTrayIcon"
                    type="outlined"
                    class="h-5 w-5"
                />
                {{ t('bat_download_image') }}
            </a>

            <div
                v-if="!productHas3DModel"
                class="flex items-center gap-3"
            >
                <div class="flex rounded-full bg-white p-0.5 shadow-md ring-1 ring-gray-200">
                    <button
                        data-test="bat-mode-2d"
                        class="rounded-full px-4 py-1 text-xs font-medium transition-all"
                        :class="[!is3DMode && 'bg-primary text-white shadow-sm', is3DMode && 'text-gray-500 hover:text-gray-700']"
                        @click="is3DMode = false"
                    >
                        2D
                    </button>
                    <button
                        data-test="bat-mode-3d"
                        class="rounded-full px-4 py-1 text-xs font-medium transition-all"
                        :class="[is3DMode && 'bg-primary text-white shadow-sm', !is3DMode && 'text-gray-500 hover:text-gray-700']"
                        @click="is3DMode = true"
                    >
                        3D
                    </button>
                </div>

                <div
                    v-if="product.format === EProductFormat.RECTO_VERSO && !is3DMode"
                    class="flex rounded-full bg-white p-0.5 shadow-md ring-1 ring-gray-200"
                >
                    <button
                        data-test="preview-side-recto"
                        class="rounded-full px-4 py-1 text-xs font-medium transition-all"
                        :class="[
                            previewSide === 'recto' && 'bg-primary text-white shadow-sm',
                            previewSide !== 'recto' && 'text-gray-500 hover:text-gray-700',
                        ]"
                        @click="previewSide = 'recto'"
                    >
                        {{ t('Recto') }}
                    </button>
                    <button
                        data-test="preview-side-verso"
                        class="rounded-full px-4 py-1 text-xs font-medium transition-all"
                        :class="[
                            previewSide === 'verso' && 'bg-primary text-white shadow-sm',
                            previewSide !== 'verso' && 'text-gray-500 hover:text-gray-700',
                        ]"
                        @click="previewSide = 'verso'"
                    >
                        {{ t('Verso') }}
                    </button>
                </div>
            </div>
        </div>
        <BaseButton
            class="absolute right-5 top-7 h-16 w-16 lg:right-8 lg:top-8 lg:h-8 lg:w-8"
            data-test="bat-modal-close"
            @click="$emit('close')"
        >
            <span class="sr-only">
                {{ t('close_popup') }}
            </span>
            <BaseIcon
                class="h-5 w-5"
                name="XMarkIcon"
            />
        </BaseButton>
    </div>
</template>
